import React from 'react';
import SearchableSelect from './SearchableSelect';

/**
 * Campos do cadastro do aluno que vêm da Ficha de Matrícula em papel da rede:
 * documentos, família e saúde. Ficam aqui, num lugar só, para o formulário de
 * Alunos não repetir a lista três vezes (vazio, edição e salvamento).
 *
 * Os "Sim/Não" guardam três estados — sim, não e não informado — para a ficha
 * impressa não marcar "Não" em quem só não respondeu.
 */

const TEXTOS = [
  'certidao_nascimento', 'cartao_sus', 'naturalidade', 'naturalidade_uf', 'municipio_residencia',
  'mae_cpf', 'mae_profissao', 'mae_telefone',
  'nome_pai', 'pai_estado_civil', 'pai_cpf', 'pai_telefone',
  'responsavel_nome', 'responsavel_parentesco', 'responsavel_telefone', 'contato_familiar',
  'alergia_qual', 'cid', 'acompanhamento_onde', 'medicacao_qual', 'medicacao_febre',
];
const SIM_NAO = [
  'vacinacao_em_dia', 'vacina_covid', 'possui_alergia', 'necessita_cuidador',
  'faz_acompanhamento', 'usa_medicacao', 'possui_diabetes',
];

export const FICHA_VAZIA = {
  ...Object.fromEntries(TEXTOS.map((c) => [c, ''])),
  ...Object.fromEntries(SIM_NAO.map((c) => [c, ''])),
  vacina_covid_doses: '',
};

const deSimNao = (v) => (v === true ? 'sim' : v === false ? 'nao' : '');
const paraSimNao = (v) => (v === 'sim' ? true : v === 'nao' ? false : null);

/** Perfil vindo da API → campos do formulário. */
export const fichaDoPerfil = (perfil = {}) => ({
  ...Object.fromEntries(TEXTOS.map((c) => [c, perfil[c] || ''])),
  ...Object.fromEntries(SIM_NAO.map((c) => [c, deSimNao(perfil[c])])),
  vacina_covid_doses: perfil.vacina_covid_doses ? String(perfil.vacina_covid_doses) : '',
});

/** Campos do formulário → o que o perfil grava. */
export const fichaParaSalvar = (form) => ({
  ...Object.fromEntries(TEXTOS.map((c) => [c, (form[c] || '').trim() || null])),
  ...Object.fromEntries(SIM_NAO.map((c) => [c, paraSimNao(form[c])])),
  vacina_covid_doses: form.vacina_covid_doses ? Number(form.vacina_covid_doses) : null,
});

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB',
  'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const OPCOES_SIM_NAO = [{ value: 'sim', label: 'Sim' }, { value: 'nao', label: 'Não' }];

function Rotulo({ children, darkMode }) {
  return (
    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
      {children}
    </label>
  );
}

function Texto({ campo, rotulo, placeholder, formData, setFormData, darkMode, className = '' }) {
  return (
    <div className={className}>
      <Rotulo darkMode={darkMode}>{rotulo}</Rotulo>
      <input
        type="text"
        value={formData[campo] || ''}
        onChange={(e) => setFormData({ ...formData, [campo]: e.target.value })}
        className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
          ? 'bg-slate-700/50 border-slate-600 text-slate-200'
          : 'bg-slate-50 border-slate-200 text-slate-700'
          } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
        placeholder={placeholder}
      />
    </div>
  );
}

function SimNao({ campo, rotulo, formData, setFormData, darkMode }) {
  return (
    <div>
      <Rotulo darkMode={darkMode}>{rotulo}</Rotulo>
      <SearchableSelect
        value={formData[campo] || ''}
        onChange={(v) => setFormData({ ...formData, [campo]: v })}
        options={OPCOES_SIM_NAO}
        emptyOptionLabel="Não informado"
        darkMode={darkMode}
        className="w-full"
      />
    </div>
  );
}

function Grupo({ titulo, children, darkMode }) {
  return (
    <div>
      <h4 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        {titulo}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

/** Aba Pessoal: documentos e naturalidade que a ficha pede. */
export function CamposDocumentosDaFicha(props) {
  const { formData, setFormData, darkMode } = props;
  return (
    <>
      <Texto {...props} campo="certidao_nascimento" rotulo="Certidão de Nascimento nº" placeholder="Número da certidão" />
      <Texto {...props} campo="cartao_sus" rotulo="Cartão do SUS" placeholder="Número do cartão" />
      <Texto {...props} campo="naturalidade" rotulo="Naturalidade" placeholder="Cidade onde nasceu" />
      <div>
        <Rotulo darkMode={darkMode}>UF da Naturalidade</Rotulo>
        <SearchableSelect
          value={formData.naturalidade_uf || ''}
          onChange={(v) => setFormData({ ...formData, naturalidade_uf: v })}
          options={UFS}
          placeholder="Selecione"
          darkMode={darkMode}
          className="w-full"
        />
      </div>
    </>
  );
}

/** Aba Endereço: o nome da cidade (o cadastro só tinha o código IBGE). */
export function CampoCidadeDaFicha(props) {
  return <Texto {...props} campo="municipio_residencia" rotulo="Cidade onde mora" placeholder="Ex.: Santa Rita" />;
}

/** Aba Família: mãe, pai, responsável e outro contato. */
export function AbaFamilia(props) {
  const { darkMode } = props;
  return (
    <div className="space-y-6">
      <Grupo titulo="Mãe" darkMode={darkMode}>
        <Texto {...props} campo="nome_mae" rotulo="Nome da Mãe" placeholder="Nome completo da mãe" className="md:col-span-2" />
        <Texto {...props} campo="mae_cpf" rotulo="CPF da Mãe" placeholder="000.000.000-00" />
        <Texto {...props} campo="mae_telefone" rotulo="Telefone da Mãe" placeholder="(00) 90000-0000" />
        <Texto {...props} campo="mae_profissao" rotulo="Profissão da Mãe" className="md:col-span-2" />
      </Grupo>
      <Grupo titulo="Pai" darkMode={darkMode}>
        <Texto {...props} campo="nome_pai" rotulo="Nome do Pai" placeholder="Nome completo do pai" className="md:col-span-2" />
        <Texto {...props} campo="pai_cpf" rotulo="CPF do Pai" placeholder="000.000.000-00" />
        <Texto {...props} campo="pai_telefone" rotulo="Telefone do Pai" placeholder="(00) 90000-0000" />
        <Texto {...props} campo="pai_estado_civil" rotulo="Estado Civil do Pai" className="md:col-span-2" />
      </Grupo>
      <Grupo titulo="Responsável pela criança" darkMode={darkMode}>
        <Texto {...props} campo="responsavel_nome" rotulo="Nome do Responsável" placeholder="Quem responde pela criança" className="md:col-span-2" />
        <Texto {...props} campo="responsavel_parentesco" rotulo="Grau de Parentesco" placeholder="Ex.: Mãe, Avó, Tio" />
        <Texto {...props} campo="responsavel_telefone" rotulo="Telefone do Responsável" placeholder="(00) 90000-0000" />
        <Texto {...props} campo="responsavel_cpf" rotulo="CPF do Responsável" placeholder="000.000.000-00" />
        <Texto {...props} campo="responsavel_nis" rotulo="NIS do Responsável" placeholder="Número do NIS" />
      </Grupo>
      <Grupo titulo="Outro contato" darkMode={darkMode}>
        <Texto {...props} campo="contato_familiar" rotulo="Outra pessoa da família"
          placeholder="Nome / parentesco / nº do celular" className="md:col-span-2" />
      </Grupo>
    </div>
  );
}

/** Aba Saúde: a seção IV da ficha. */
export function AbaSaude(props) {
  const { formData, setFormData, darkMode } = props;
  return (
    <div className="space-y-6">
      <Grupo titulo="Vacinação" darkMode={darkMode}>
        <SimNao {...props} campo="vacinacao_em_dia" rotulo="Cartão de vacinação em dia?" />
        <SimNao {...props} campo="vacina_covid" rotulo="Vacinado(a) contra COVID?" />
        <div>
          <Rotulo darkMode={darkMode}>Doses contra COVID</Rotulo>
          <SearchableSelect
            value={formData.vacina_covid_doses || ''}
            onChange={(v) => setFormData({ ...formData, vacina_covid_doses: v })}
            options={[{ value: '1', label: '1ª dose' }, { value: '2', label: '2ª dose' }, { value: '3', label: '3ª dose' }]}
            emptyOptionLabel="Não informado"
            darkMode={darkMode}
            className="w-full"
          />
        </div>
      </Grupo>
      <Grupo titulo="Saúde e cuidados" darkMode={darkMode}>
        <SimNao {...props} campo="possui_alergia" rotulo="Possui algum tipo de alergia?" />
        <Texto {...props} campo="alergia_qual" rotulo="Qual alergia?" />
        <SimNao {...props} campo="usa_medicacao" rotulo="Faz uso de medicação?" />
        <Texto {...props} campo="medicacao_qual" rotulo="Qual medicação e para quê?" />
        <SimNao {...props} campo="faz_acompanhamento" rotulo="Faz acompanhamento?" />
        <Texto {...props} campo="acompanhamento_onde" rotulo="Onde faz acompanhamento?" />
        <SimNao {...props} campo="necessita_cuidador" rotulo="Necessita de cuidador escolar?" />
        <Texto {...props} campo="cid" rotulo="Código do CID" placeholder="Ex.: F84.0" />
        <SimNao {...props} campo="possui_diabetes" rotulo="Possui diabetes?" />
        <Texto {...props} campo="medicacao_febre" rotulo="Em caso de febre, o que pode tomar?" />
      </Grupo>
      <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        As deficiências e o AEE ficam na aba Necessidades. Tudo aqui é opcional e sai na Ficha de Matrícula.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leitura, no Perfil do Aluno (aba Dados Pessoais)
// ---------------------------------------------------------------------------

const simNaoTexto = (v) => (v === true ? 'Sim' : v === false ? 'Não' : '-');

function Item({ rotulo, valor, darkMode, className = '' }) {
  return (
    <div className={className}>
      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{rotulo}</span>
      <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{valor || '-'}</p>
    </div>
  );
}

function Cartao({ titulo, children, darkMode }) {
  return (
    <div className={`rounded-xl p-6 ${darkMode
      ? 'bg-slate-800/50 border border-slate-700'
      : 'bg-white border border-slate-200 shadow-sm'
      }`}>
      <h4 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
        {titulo}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

/** Itens a mais no cartão "Dados Pessoais": certidão, SUS e naturalidade. */
export function ItensDocumentosDaFicha({ aluno, darkMode }) {
  const natural = [aluno.naturalidade, aluno.naturalidade_uf].filter(Boolean).join(' / ');
  return (
    <>
      <Item rotulo="Certidão de Nascimento nº" valor={aluno.certidao_nascimento} darkMode={darkMode} />
      <Item rotulo="Cartão do SUS" valor={aluno.cartao_sus} darkMode={darkMode} />
      <Item rotulo="Naturalidade" valor={natural} darkMode={darkMode} />
    </>
  );
}

/** Cartões "Família" e "Saúde" com o que a Ficha de Matrícula pede. */
export function CartoesFamiliaESaude({ aluno, darkMode }) {
  const comTel = (nome, tel) => [nome, tel].filter(Boolean).join(' • ');
  const com = (resposta, detalhe) => (resposta === true && detalhe ? `Sim — ${detalhe}` : simNaoTexto(resposta));
  return (
    <>
      <Cartao titulo="👨‍👩‍👧 Família" darkMode={darkMode}>
        <Item rotulo="Mãe" valor={comTel(aluno.nome_mae, aluno.mae_telefone)} darkMode={darkMode} />
        <Item rotulo="CPF da Mãe" valor={aluno.mae_cpf} darkMode={darkMode} />
        <Item rotulo="Profissão da Mãe" valor={aluno.mae_profissao} darkMode={darkMode} />
        <Item rotulo="Pai" valor={comTel(aluno.nome_pai, aluno.pai_telefone)} darkMode={darkMode} />
        <Item rotulo="CPF do Pai" valor={aluno.pai_cpf} darkMode={darkMode} />
        <Item rotulo="Estado Civil do Pai" valor={aluno.pai_estado_civil} darkMode={darkMode} />
        <Item
          rotulo="Responsável"
          valor={[aluno.responsavel_nome, aluno.responsavel_parentesco && `(${aluno.responsavel_parentesco})`, aluno.responsavel_telefone].filter(Boolean).join(' ')}
          darkMode={darkMode}
        />
        <Item rotulo="CPF do Responsável" valor={aluno.responsavel_cpf} darkMode={darkMode} />
        <Item rotulo="NIS do Responsável" valor={aluno.responsavel_nis} darkMode={darkMode} />
        <Item rotulo="Outro contato" valor={aluno.contato_familiar} darkMode={darkMode} className="md:col-span-2 lg:col-span-3" />
      </Cartao>
      <Cartao titulo="🩺 Saúde" darkMode={darkMode}>
        <Item rotulo="Vacinação em dia" valor={simNaoTexto(aluno.vacinacao_em_dia)} darkMode={darkMode} />
        <Item
          rotulo="Vacina contra COVID"
          valor={aluno.vacina_covid === true && aluno.vacina_covid_doses ? `Sim — ${aluno.vacina_covid_doses}ª dose` : simNaoTexto(aluno.vacina_covid)}
          darkMode={darkMode}
        />
        <Item rotulo="Alergia" valor={com(aluno.possui_alergia, aluno.alergia_qual)} darkMode={darkMode} />
        <Item rotulo="Medicação" valor={com(aluno.usa_medicacao, aluno.medicacao_qual)} darkMode={darkMode} />
        <Item rotulo="Acompanhamento" valor={com(aluno.faz_acompanhamento, aluno.acompanhamento_onde)} darkMode={darkMode} />
        <Item rotulo="Cuidador escolar" valor={simNaoTexto(aluno.necessita_cuidador)} darkMode={darkMode} />
        <Item rotulo="CID" valor={aluno.cid} darkMode={darkMode} />
        <Item rotulo="Diabetes" valor={simNaoTexto(aluno.possui_diabetes)} darkMode={darkMode} />
        <Item rotulo="Em caso de febre" valor={aluno.medicacao_febre} darkMode={darkMode} />
      </Cartao>
    </>
  );
}
